import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings, CalendarClock, Download, ShieldAlert } from 'lucide-react';
import { getApiBaseUrl, getAllClients } from '../../services/api';

// ── Solution Partner platform settings (super only) ────────────────────
// The booking link customers receive when they choose "scope with a
// Solution Partner", the email that gets the partner brief, and the
// deep-scoping price. Stored server-side in platform_settings; env vars
// remain the fallback until saved here.

type PartnerSettings = {
  solution_partner_cal_url: string;
  solution_partner_email: string;
  deep_scoping_price_cents: number;
};

const SolutionPartnerCard: React.FC = () => {
  const [settings, setSettings] = useState<PartnerSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [priceUsd, setPriceUsd] = useState<string>('');

  useEffect(() => {
    axios
      .get<{ settings: PartnerSettings }>(`${getApiBaseUrl()}/super/platform-settings`)
      .then((r) => {
        setSettings(r.data.settings);
        setPriceUsd(String(Math.round((r.data.settings.deep_scoping_price_cents || 0) / 100)));
      })
      .catch(() => setNote("Couldn't load platform settings."));
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setNote(null);
    try {
      const cents = Math.max(0, Math.round(Number(priceUsd || '0') * 100));
      const r = await axios.put<{ settings: PartnerSettings }>(
        `${getApiBaseUrl()}/super/platform-settings`,
        {
          solution_partner_cal_url: settings.solution_partner_cal_url,
          solution_partner_email: settings.solution_partner_email,
          deep_scoping_price_cents: cents,
        },
      );
      setSettings(r.data.settings);
      setPriceUsd(String(Math.round((r.data.settings.deep_scoping_price_cents || 0) / 100)));
      setNote('Saved.');
    } catch (e: any) {
      setNote(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-card border border-surface-border shadow-card mb-6">
      <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
        <CalendarClock size={16} className="text-gray-400" />
        <h2 className="text-sm font-semibold text-gray-900">Solution Partner</h2>
      </div>
      <div className="p-5 space-y-4">
        {!settings ? (
          <div className="text-sm text-gray-500">{note || 'Loading…'}</div>
        ) : (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Booking link (cal.com)
              </label>
              <input
                type="url"
                value={settings.solution_partner_cal_url || ''}
                onChange={(e) => setSettings({ ...settings, solution_partner_cal_url: e.target.value })}
                placeholder="https://cal.com/team-brndsh/scoping"
                className="w-full max-w-lg rounded-md border border-surface-border px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Customers who choose "scope with a Solution Partner" pick a time here.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Partner email
              </label>
              <input
                type="email"
                value={settings.solution_partner_email || ''}
                onChange={(e) => setSettings({ ...settings, solution_partner_email: e.target.value })}
                placeholder="team@brndsh.io"
                className="w-full max-w-lg rounded-md border border-surface-border px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Gets the booking brief with the direct link to the client's problem.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Deep scoping price (USD)
              </label>
              <input
                type="number"
                min={0}
                value={priceUsd}
                onChange={(e) => setPriceUsd(e.target.value)}
                className="w-40 rounded-md border border-surface-border px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                One-time Stripe checkout amount for paid deep scoping.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={save}
                disabled={saving}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              {note && <span className="text-xs text-gray-500">{note}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Scoping plugin download (super always; consultants when enabled) ───
// The zip carries a personal live credential — the card says so plainly and
// re-download doubles as revocation. Supers additionally control WHO can
// download (platform setting): team only (default), all consultants, or a
// hand-picked email list.

// The remote connector address consultants paste into Claude. One address for
// every Claude surface; auth happens by signing in here, so it carries no
// credential and is safe to display and share.
const MCP_URL = (process.env.REACT_APP_BUILDER_URL || 'https://builder.agentsanywhere.ai').replace(/\/+$/, '') + '/api/mcp/http';

const ScopingPluginCard: React.FC<{ isSuper: boolean }> = ({ isSuper }) => {
  const [allowed, setAllowed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // Optional per-client pin for the download
  const [clients, setClients] = useState<Array<{ id: string; name: string; email?: string }>>([]);
  const [pinClientId, setPinClientId] = useState<string>('');
  // Super-only access controls
  const [accessMode, setAccessMode] = useState<string>('super');
  const [allowedEmails, setAllowedEmails] = useState<string>('');
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessNote, setAccessNote] = useState<string | null>(null);
  // Website (public) zip — super only
  const [webRevoke, setWebRevoke] = useState(false);
  const [webDownloading, setWebDownloading] = useState(false);
  const [webNote, setWebNote] = useState<string | null>(null);

  useEffect(() => {
    axios
      .get<{ allowed: boolean }>(`${getApiBaseUrl()}/plugin/access`)
      .then((r) => {
        setAllowed(r.data.allowed);
        if (r.data.allowed) {
          // Populate the optional per-client pin picker. A load failure just
          // leaves the default "all clients" option — never blocks download.
          getAllClients()
            .then((list: any[]) => setClients(Array.isArray(list) ? list : []))
            .catch(() => setClients([]));
        }
      })
      .catch(() => setAllowed(false));
    if (isSuper) {
      axios
        .get<{ settings: Record<string, any> }>(`${getApiBaseUrl()}/super/platform-settings`)
        .then((r) => {
          setAccessMode(r.data.settings.plugin_download_access || 'super');
          setAllowedEmails(((r.data.settings.plugin_download_allowed_emails as string[]) || []).join('\n'));
        })
        .catch(() => {});
    }
  }, [isSuper]);

  const download = async () => {
    setDownloading(true);
    setNote(null);
    try {
      const endpoint = pinClientId
        ? `${getApiBaseUrl()}/plugin/download?client_id=${encodeURIComponent(pinClientId)}`
        : `${getApiBaseUrl()}/plugin/download`;
      const r = await axios.get(endpoint, { responseType: 'blob' });
      const disposition = r.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || 'orbit-scope.zip';
      a.click();
      URL.revokeObjectURL(url);
      setNote('Downloaded. This copy holds your fresh credential — any previous download is now revoked.');
    } catch {
      setNote("Download failed — check that the builder is reachable, or contact team@brndsh.io.");
    } finally {
      setDownloading(false);
    }
  };

  const saveAccess = async () => {
    setSavingAccess(true);
    setAccessNote(null);
    try {
      const emails = allowedEmails
        .split(/[\n,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean);
      await axios.put(`${getApiBaseUrl()}/super/platform-settings`, {
        plugin_download_access: accessMode,
        plugin_download_allowed_emails: emails,
      });
      setAccessNote('Saved.');
    } catch (e: any) {
      setAccessNote(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSavingAccess(false);
    }
  };

  if (!allowed && !isSuper) return null;

  return (
    <div className="bg-white rounded-card border border-surface-border shadow-card mb-6">
      <div className="px-5 py-4 border-b border-surface-border flex items-center gap-2">
        <Download size={16} className="text-gray-400" />
        <h2 className="text-sm font-semibold text-gray-900">Scoping plugin (Orbit)</h2>
      </div>
      <div className="p-5 space-y-4">
        {allowed && (
          <>
            {/* The connector IS the product now (2026-08-01): one address, sign
                in with this account, no file and no credential to handle. */}
            <div className="rounded-md border border-brand-200 bg-brand-50/60 px-4 py-4 max-w-2xl">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Use it inside Claude — nothing to download</h3>
              <p className="text-xs text-gray-600 mb-3">
                Add this address in Claude, sign in with this account when it asks, and your
                scoping tools are there. Works in Claude on the web, desktop and Claude Code.
              </p>
              <div className="flex items-center gap-2 mb-3">
                <code className="flex-1 truncate rounded-md border border-surface-border bg-white px-3 py-2 text-xs font-sans text-gray-800">
                  {MCP_URL}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(MCP_URL);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="shrink-0 rounded-md bg-brand-600 px-3 py-2 text-xs font-medium text-white hover:bg-brand-700"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <ol className="text-xs text-gray-600 space-y-1 list-decimal pl-4">
                <li>In Claude: Settings → Connectors → Add custom connector</li>
                <li>Paste the address above and save</li>
                <li>Claude sends you here to sign in and approve — then you're connected</li>
              </ol>
              <p className="text-xs text-gray-400 mt-2">
                No key to copy or keep safe. Approve once; you can disconnect from Claude at any time.
              </p>
            </div>

            <div className="pt-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Prefer a local install?</h3>
              <p className="text-sm text-gray-600 max-w-2xl">
                The download is for running Orbit locally in Claude Code. It is personal:
                it bakes in your scoping credential, and the plugin will ask which client each
                session is for before anything is saved.
              </p>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 max-w-2xl">
              <div className="flex items-center gap-2 text-amber-800 text-xs font-semibold mb-1">
                <ShieldAlert size={14} /> Safety
              </div>
              <ul className="text-xs text-amber-800 space-y-1 list-disc pl-4">
                <li>The zip contains a live credential — never share, email, or commit it.</li>
                <li>Downloading again issues a fresh credential and revokes the previous one.</li>
                <li>Confirm which client a session is for before sharing client information.</li>
              </ul>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Download for a specific client (optional)
              </label>
              <select
                value={pinClientId}
                onChange={(e) => setPinClientId(e.target.value)}
                className="w-full max-w-lg rounded-md border border-surface-border px-3 py-2 text-sm bg-white"
              >
                <option value="">All clients — Orbit asks which client each session</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.email ? ` — ${c.email}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1 max-w-lg">
                A pinned download skips the which-client step — every session lands on
                this client's dossier, and the credential can't touch any other client.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={download}
                disabled={downloading}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {downloading ? 'Preparing…' : 'Download plugin'}
              </button>
              {note && <span className="text-xs text-gray-500 max-w-md">{note}</span>}
            </div>
            <p className="text-xs text-gray-400 max-w-2xl">
              Full setup steps and safety notes are inside the zip (INSTRUCTIONS.md, SAFETY.md).
            </p>
          </>
        )}

        {isSuper && (
          <div className={allowed ? 'pt-4 border-t border-surface-border' : ''}>
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Who can download this plugin
            </label>
            <div className="space-y-2 mb-3">
              {[
                { v: 'super', label: 'Only super users (team@brndsh.io)', hint: 'Default — the download stays internal.' },
                { v: 'all', label: 'All consultants', hint: 'Every consultant sees the download card in their settings.' },
                { v: 'selected', label: 'Selected consultants', hint: 'Only the emails listed below.' },
              ].map((o) => (
                <label key={o.v} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="plugin-access"
                    checked={accessMode === o.v}
                    onChange={() => setAccessMode(o.v)}
                    className="mt-0.5"
                  />
                  <span>
                    {o.label}
                    <span className="block text-xs text-gray-400">{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {accessMode === 'selected' && (
              <textarea
                value={allowedEmails}
                onChange={(e) => setAllowedEmails(e.target.value)}
                placeholder={'consultant1@example.com\nconsultant2@example.com'}
                rows={3}
                className="w-full max-w-lg rounded-md border border-surface-border px-3 py-2 text-sm mb-3 font-sans"
              />
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={saveAccess}
                disabled={savingAccess}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {savingAccess ? 'Saving…' : 'Save access'}
              </button>
              {accessNote && <span className="text-xs text-gray-500">{accessNote}</span>}
            </div>

            <div className="pt-4 mt-4 border-t border-surface-border">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Website plugin (public)
              </label>
              <p className="text-xs text-gray-500 max-w-2xl mb-2">
                The zip for the marketing site: visitors register themselves (name + email)
                and their scoping lands on the team's client list. Its token is register-only —
                it can never read or touch existing data.
              </p>
              <label className="flex items-center gap-2 text-xs text-gray-600 mb-3 cursor-pointer">
                <input type="checkbox" checked={webRevoke} onChange={(e) => setWebRevoke(e.target.checked)} />
                Revoke previously distributed copies (rotates the token — old zips stop working)
              </label>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    setWebDownloading(true);
                    setWebNote(null);
                    try {
                      const r = await axios.get(
                        `${getApiBaseUrl()}/plugin/download-web?rotate=${webRevoke}`,
                        { responseType: 'blob' },
                      );
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'orbit-scope-website.zip';
                      a.click();
                      URL.revokeObjectURL(url);
                      setWebNote(webRevoke
                        ? 'Downloaded with a fresh token — previously shared copies are now dead.'
                        : 'Downloaded. Existing distributed copies keep working.');
                    } catch {
                      setWebNote('Download failed — check that the builder is reachable.');
                    } finally {
                      setWebDownloading(false);
                    }
                  }}
                  disabled={webDownloading}
                  className="rounded-md border border-surface-border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {webDownloading ? 'Preparing…' : 'Download website plugin'}
                </button>
                {webNote && <span className="text-xs text-gray-500 max-w-md">{webNote}</span>}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────

const ConsultantSettings: React.FC = () => {
  const userName = localStorage.getItem('user_name') || 'Consultant';
  const userEmail = localStorage.getItem('user_email') || '';
  const userRole = localStorage.getItem('user_role') || '';

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Settings size={22} className="text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        </div>
        <p className="text-sm text-gray-500 ml-[34px]">
          Manage your account and integrations
        </p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-card border border-surface-border shadow-card mb-6">
        <div className="px-5 py-4 border-b border-surface-border">
          <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 text-lg font-semibold shrink-0">
              {userName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-base font-semibold text-gray-900">{userName}</div>
              {userEmail && (
                <div className="text-sm text-gray-500">{userEmail}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Feature flags — super only */}
      {userRole === 'super' && <PlatformFlagsCard />}

      {/* Solution Partner platform settings — super only */}
      {userRole === 'super' && <SolutionPartnerCard />}

      {/* Scoping plugin download — super always; consultants when enabled */}
      <ScopingPluginCard isSuper={userRole === 'super'} />
    </div>
  );
};

const PlatformFlagsCard: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    axios.get<{ settings: Record<string, any> }>(`${getApiBaseUrl()}/super/platform-settings`)
      .then((r) => {
        const v = r.data.settings?.invite_context_enabled;
        setEnabled(v === true || String(v).toLowerCase() === 'true');
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const toggle = async () => {
    const next = !enabled;
    setSaving(true);
    setNote(null);
    try {
      await axios.put(`${getApiBaseUrl()}/super/platform-settings`, {
        invite_context_enabled: next ? 'true' : 'false',
      });
      setEnabled(next);
      setNote(next ? 'On — invite context + bulk upload are now visible on team screens.' : 'Off — team screens are back to the standard invite.');
    } catch (e: any) {
      setNote(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-card border border-surface-border shadow-card mb-6">
      <div className="px-5 py-4 border-b border-surface-border">
        <h2 className="text-sm font-semibold text-gray-900">Feature flags</h2>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-gray-900">Invite context &amp; bulk upload</div>
            <div className="text-xs text-gray-500 mt-0.5 max-w-md">
              Show the per-invite context fields (level, title), CSV bulk upload on team screens, and the industry field on new-client — all prefill the dossier for the invitee to confirm.
            </div>
          </div>
          <button
            onClick={toggle}
            disabled={!loaded || saving}
            role="switch"
            aria-checked={enabled}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${enabled ? 'bg-brand-600' : 'bg-gray-300'} disabled:opacity-50`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform mt-0.5 ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        {note && <div className="text-xs text-gray-500 mt-3">{note}</div>}
      </div>
    </div>
  );
};

export default ConsultantSettings;
